import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Spinner, Input, Label, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { API_BASE, getAuthHeaders } from "../../../helpers/apiConfig";
// OData naive-UTC datetime'ları +03:00 etiketiyle döndürüyor; ham new Date()
// kullanmak saatleri 3 saat geriden gösterir (bkz. helpers/apiDate.js).
import { formatApiDateTime } from "../../../helpers/apiDate";

/**
 * Haber Yönetimi — Hangfire job'ın (HaberJobs) çektiği başlıkların moderasyonu.
 *
 * SATIRLARIN SAHİBİ JOB'DUR. Panelden haber EKLENMEZ ve metni DÜZENLENMEZ:
 *  • Job bir sonraki çekimde başlığı/özeti kaynaktan gelen hâliyle geri yazar,
 *  • daha önemlisi, metni değiştirmek kaynağa ait olmayan bir yazıyı onun adıyla
 *    yayınlamak olur (atıf/telif sorunu).
 * Bu yüzden sunucu da yalnızca AKTIF ve ÖNE ÇIKAR alanlarını kabul eder.
 *
 * SAYFALAMA SUNUCUDA: tablo job ile sürekli büyüyor, API tek yanıtta en fazla
 * 100 satır döndürüyor. İstemci tarafı sayfalama kullanılsaydı liste sessizce
 * ilk 100 satırda kesilir, "haber kayboldu" gibi görünürdü.
 */

const BASE_URL = API_BASE;
const SAYFA_BOYU = 50;

/** OData string literal'inde tek tırnak ikilenir; yoksa filtre 400 döner. */
const odataMetin = (deger) => `'${String(deger).replace(/'/g, "''")}'`;

const HaberYonetimi = () => {
  document.title = "Haber Yönetimi | HarmanaGEL";

  const [haberler, setHaberler] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [sayfa, setSayfa] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [kaynaklar, setKaynaklar] = useState([]);
  const [kaynakFiltre, setKaynakFiltre] = useState("");
  const [durumFiltre, setDurumFiltre] = useState("");   // "" | "aktif" | "pasif" | "one"
  const [aramaGirdi, setAramaGirdi] = useState("");
  const [arama, setArama] = useState("");

  const bildir = (mesaj) => {
    setSuccessMsg(mesaj);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const hataMesaji = async (response, varsayilan) => {
    try {
      const metin = await response.text();
      if (!metin) return varsayilan;
      try {
        const json = JSON.parse(metin);
        return json?.error?.message || json?.Message || json?.message || varsayilan;
      } catch {
        return metin;
      }
    } catch {
      return varsayilan;
    }
  };

  // Kaynak listesi filtre açılırı için; haber listesinden bağımsız, bir kez çekilir.
  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/HABER_KAYNAKLARI?$orderby=AD asc&$select=ID,AD,AKTIF`,
          { headers: getAuthHeaders() }
        );
        if (!response.ok) return;
        const result = await response.json();
        setKaynaklar(result?.value || []);
      } catch {
        /* filtre açılırı kritik değil */
      }
    })();
  }, []);

  const fetchHaberler = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filtreler = [];
      if (kaynakFiltre) filtreler.push(`KAYNAK_ID eq ${parseInt(kaynakFiltre, 10)}`);
      if (durumFiltre === "aktif") filtreler.push("AKTIF eq true");
      if (durumFiltre === "pasif") filtreler.push("AKTIF eq false");
      if (durumFiltre === "one") filtreler.push("ONE_CIKAR eq true");
      if (arama.trim().length >= 2) {
        const q = odataMetin(arama.trim());
        filtreler.push(`(contains(BASLIK,${q}) or contains(OZET,${q}))`);
      }

      const parcalar = [
        "$expand=HABER_KAYNAKLARI($select=ID,AD,AKTIF)",
        "$orderby=YAYIN_TARIHI desc",
        `$top=${SAYFA_BOYU}`,
        `$skip=${sayfa * SAYFA_BOYU}`,
        "$count=true",
      ];
      if (filtreler.length) parcalar.push(`$filter=${encodeURIComponent(filtreler.join(" and "))}`);

      const response = await fetch(`${BASE_URL}/HABERLER?${parcalar.join("&")}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await hataMesaji(response, `HTTP ${response.status}`));
      const result = await response.json();
      setHaberler(result?.value || []);
      setToplam(result?.["@odata.count"] ?? result?.["@count"] ?? 0);
    } catch (err) {
      setError(err.message || "Haberler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [kaynakFiltre, durumFiltre, arama, sayfa]);

  useEffect(() => { fetchHaberler(); }, [fetchHaberler]);

  // Filtre değişince ilk sayfaya dön; yoksa 5. sayfada 3 sonuçlu bir filtre
  // uygulandığında boş ekran görünürdü.
  const filtreDegistir = (ayarla) => (deger) => {
    setSayfa(0);
    ayarla(deger);
  };

  const araAtesle = () => {
    setSayfa(0);
    setArama(aramaGirdi);
  };

  /** Tek alan güncellemesi: sunucu yalnızca AKTIF ve ONE_CIKAR'ı kabul eder. */
  const alanDegistir = async (haber, alan, deger, mesaj) => {
    try {
      const response = await fetch(`${BASE_URL}/HABERLER(${haber.ID})`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ [alan]: deger }),
      });
      if (!response.ok) throw new Error(await hataMesaji(response, "Güncellenemedi."));
      setHaberler((onceki) =>
        onceki.map((h) => (h.ID === haber.ID ? { ...h, [alan]: deger } : h))
      );
      bildir(mesaj);
    } catch (err) {
      setError(err.message || "Güncellenemedi.");
    }
  };

  const handleDelete = async (haber) => {
    if (!window.confirm(
      `"${haber.BASLIK}" kalıcı olarak silinecek.\n\n` +
      "Not: Job aynı haberi bir sonraki çekimde YENİDEN yazabilir. " +
      "Kalıcı gizleme için silmek yerine pasife alın."
    )) return;
    try {
      const response = await fetch(`${BASE_URL}/HABERLER(${haber.ID})`, {
        method: "DELETE", headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(await hataMesaji(response, "Silinemedi."));
      fetchHaberler();
      bildir("Haber silindi.");
    } catch (err) {
      setError(err.message || "Silinemedi.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "Haber",
      accessorKey: "BASLIK",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const h = cellProps.row.original;
        return (
          <div className="d-flex align-items-start gap-2" style={{ maxWidth: 520 }}>
            {h.GORSEL_URL ? (
              <img
                src={h.GORSEL_URL}
                alt=""
                style={{ height: 46, width: 70, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                onError={(e) => { e.target.style.visibility = "hidden"; }}
              />
            ) : null}
            <div>
              <a
                href={h.ORIJINAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="fw-semibold d-block"
              >
                {h.BASLIK}
              </a>
              {h.OZET ? (
                <div className="text-muted small text-truncate" style={{ maxWidth: 430 }} title={h.OZET}>
                  {h.OZET}
                </div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      header: "Kaynak",
      accessorKey: "KAYNAK_ID",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const h = cellProps.row.original;
        const kaynak = h.HABER_KAYNAKLARI;
        if (!kaynak) return <span className="text-muted">-</span>;
        return (
          <div>
            <div className="small">{kaynak.AD}</div>
            {/* Kaynağı pasif olan haber, kendisi AKTIF olsa da uygulamada GÖRÜNMEZ. */}
            {!kaynak.AKTIF && (
              <Badge color="warning" className="bg-warning-subtle text-warning">
                kaynak pasif
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      header: "Kategori",
      accessorKey: "KATEGORI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => cell.getValue()
        ? <Badge color="info" className="bg-info-subtle text-info">{cell.getValue()}</Badge>
        : <span className="text-muted">-</span>,
    },
    {
      header: "Tarih",
      accessorKey: "YAYIN_TARIHI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => <span className="small">{formatApiDateTime(cell.getValue())}</span>,
    },
    {
      header: "İlgi",
      accessorKey: "OKUNMA_SAYISI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const h = cellProps.row.original;
        return (
          <div className="small text-muted text-nowrap">
            <div><i className="bx bx-show me-1" />{h.OKUNMA_SAYISI ?? 0}</div>
            <div><i className="bx bx-heart me-1" />{h.BEGENI_SAYISI ?? 0}</div>
            <div><i className="bx bx-bookmark me-1" />{h.KAYDETME_SAYISI ?? 0}</div>
          </div>
        );
      },
    },
    {
      header: "Durum",
      accessorKey: "AKTIF",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const h = cellProps.row.original;
        return (
          <div className="d-flex flex-column gap-1">
            <Badge
              color={h.AKTIF ? "success" : "secondary"}
              style={{ cursor: "pointer" }}
              onClick={() => alanDegistir(h, "AKTIF", !h.AKTIF, h.AKTIF ? "Haber gizlendi." : "Haber yayına alındı.")}
              title="Durumu değiştirmek için tıklayın"
            >
              {h.AKTIF ? "Yayında" : "Gizli"}
            </Badge>
            <Badge
              color={h.ONE_CIKAR ? "warning" : "light"}
              className={h.ONE_CIKAR ? "" : "text-dark"}
              style={{ cursor: "pointer" }}
              onClick={() => alanDegistir(h, "ONE_CIKAR", !h.ONE_CIKAR, h.ONE_CIKAR ? "Öne çıkarma kaldırıldı." : "Haber öne çıkarıldı.")}
              title="Öne çıkarılan haberler listede en üstte gösterilir ve otomatik temizlikten muaftır"
            >
              <i className="bx bx-star me-1" />{h.ONE_CIKAR ? "Öne çıkan" : "Normal"}
            </Badge>
          </div>
        );
      },
    },
    {
      header: "",
      id: "islemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => (
        <Button color="danger" size="sm" outline onClick={() => handleDelete(cellProps.row.original)} title="Sil">
          <i className="bx bx-trash" />
        </Button>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const sonSayfa = Math.max(0, Math.ceil(toplam / SAYFA_BOYU) - 1);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Haber Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h4 className="card-title mb-1">Haberler</h4>
                      <p className="text-muted small mb-0">
                        Toplam {toplam} haber. Başlık ve özet kaynağa aittir, düzenlenemez —
                        yalnızca gizleme ve öne çıkarma yapılır.
                      </p>
                    </div>
                    <Button color="light" size="sm" onClick={fetchHaberler} title="Yenile">
                      <i className="bx bx-refresh" />
                    </Button>
                  </div>

                  <Row className="mb-3 g-2">
                    <Col md={3}>
                      <Label className="form-label small mb-1">Kaynak</Label>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={kaynakFiltre}
                        onChange={(e) => filtreDegistir(setKaynakFiltre)(e.target.value)}
                      >
                        <option value="">Tüm kaynaklar</option>
                        {kaynaklar.map((k) => (
                          <option key={k.ID} value={k.ID}>
                            {k.AD}{k.AKTIF ? "" : " (pasif)"}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Label className="form-label small mb-1">Durum</Label>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={durumFiltre}
                        onChange={(e) => filtreDegistir(setDurumFiltre)(e.target.value)}
                      >
                        <option value="">Tümü</option>
                        <option value="aktif">Yayında</option>
                        <option value="pasif">Gizli</option>
                        <option value="one">Öne çıkanlar</option>
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label className="form-label small mb-1">Ara (başlık / özet)</Label>
                      <div className="d-flex gap-2">
                        <Input
                          bsSize="sm"
                          value={aramaGirdi}
                          placeholder="en az 2 karakter"
                          onChange={(e) => setAramaGirdi(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") araAtesle(); }}
                        />
                        <Button color="primary" size="sm" onClick={araAtesle}>
                          <i className="bx bx-search" />
                        </Button>
                        {arama ? (
                          <Button color="light" size="sm" onClick={() => { setAramaGirdi(""); setArama(""); setSayfa(0); }}>
                            <i className="bx bx-x" />
                          </Button>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : haberler.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="bx bx-news fs-1 d-block mb-2" />
                      Bu filtreyle haber yok.
                    </div>
                  ) : (
                    <>
                      <TableContainer
                        columns={columns}
                        data={haberler}
                        isGlobalFilter={false}
                        isPagination={false}
                        isCustomPageSize={false}
                        tableClass="table-bordered align-middle w-100 mb-0"
                        theadClass="table-light"
                      />

                      {/* Sayfalama sunucu tarafında — TableContainer'ınki yalnızca
                          yüklü 50 satırı böler, gerçek sayfalamayı temsil etmez. */}
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="text-muted small">
                          {toplam === 0 ? 0 : sayfa * SAYFA_BOYU + 1}
                          –{Math.min((sayfa + 1) * SAYFA_BOYU, toplam)} / {toplam}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light" size="sm"
                            disabled={sayfa === 0}
                            onClick={() => setSayfa((s) => Math.max(0, s - 1))}
                          >
                            <i className="bx bx-chevron-left" /> Önceki
                          </Button>
                          <Button
                            color="light" size="sm"
                            disabled={sayfa >= sonSayfa}
                            onClick={() => setSayfa((s) => s + 1)}
                          >
                            Sonraki <i className="bx bx-chevron-right" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default HaberYonetimi;
